import React from 'react';
import { Box } from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import { LiveDot } from './LiveDot';

export type StatusTone = 'good' | 'warning' | 'bad' | 'neutral';

export interface StatusChipProps {
  tone: StatusTone;
  /** Says the state in words — the colour and icon only repeat it. */
  label: string;
  /** Happening right now: a pulsing dot in place of the icon. */
  pulse?: boolean;
  /**
   * The word alone, no icon: a tag on a heading ("Work in progress") rather
   * than a state beside a figure, where an icon would read as a second info
   * icon beside the heading's own.
   */
  plain?: boolean;
}

const ICONS: Record<StatusTone, SvgIconComponent> = {
  good: CheckCircleRounded,
  warning: ErrorOutlineRounded,
  bad: ErrorOutlineRounded,
  neutral: RemoveCircleOutlineRounded,
};

/**
 * A short state beside a figure: "Reaches target", "Short of target".
 *
 * Always an icon and a word as well as a colour, so the state reads for
 * someone who cannot tell the green from the red, and in print.
 */
export function StatusChip({ tone, label, pulse = false, plain = false }: StatusChipProps) {
  const Icon = ICONS[tone];
  return (
    <Box
      component="span"
      sx={(t) => {
        const ink = {
          good: t.tokens.semantic.success,
          warning: t.tokens.semantic.warning,
          bad: t.tokens.semantic.danger,
          neutral: t.tokens.text.secondary,
        }[tone];
        const fill = {
          good: t.tokens.semantic.successSoft,
          warning: t.tokens.semantic.warningSoft,
          bad: t.tokens.semantic.dangerSoft,
          neutral: t.tokens.surface.sunken,
        }[tone];
        return {
          ...t.typography.caption,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: `${t.ds.radius.full}px`,
          bgcolor: fill,
          color: ink,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        };
      }}
    >
      {pulse
        ? <Box component="span" sx={{ display: 'inline-flex', px: 0.25 }}><LiveDot size={7} /></Box>
        : plain ? null : <Icon sx={(t) => ({ fontSize: t.typography.body2.fontSize })} aria-hidden />}
      {label}
    </Box>
  );
}
